import { privateApi } from '@/config/axios.config';

const API_BASE = '/condominium/collaborators';

export interface Collaborator {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  status: 'active' | 'inactive' | 'pending' | 'on_leave';
  hireDate: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CollaboratorCreateDTO {
  name: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  status: 'active' | 'inactive' | 'pending' | 'on_leave';
  hireDate: string;
}

export interface CollaboratorUpdateDTO extends Partial<CollaboratorCreateDTO> {
  id?: string;
}

export interface CollaboratorFilters {
  status?: string;
  department?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CollaboratorStats {
  total: number;
  active: number;
  inactive: number;
  pending: number;
  onLeave: number;
}

export const collaboratorsApiService = {
  // Obtener todos los colaboradores con filtros
  async getCollaborators(filters: CollaboratorFilters = {}): Promise<{
    collaborators: Collaborator[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.department) params.append('department', filters.department);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await privateApi.get(`${API_BASE}?${params.toString()}`);
    
    // El interceptor transforma la respuesta paginada en:
    // { success: true, data: { collaborators: [], total, page, limit, totalPages }, pagination: {...} }
    
    // Extraer data del wrapper del interceptor
    const data = response.data?.data || response.data;
    
    // Si tiene collaborators directamente, devolverlo
    if (data && typeof data === 'object' && 'collaborators' in data) {
      return {
        collaborators: Array.isArray(data.collaborators) ? data.collaborators : [],
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 10,
        totalPages: data.totalPages || 1,
      };
    }
    
    // Si viene como array directo (legacy), convertir
    if (Array.isArray(data)) {
      return {
        collaborators: data,
        total: data.length,
        page: 1,
        limit: 10,
        totalPages: 1,
      };
    }
    
    // Fallback: devolver estructura vacía
    return {
      collaborators: [],
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    };
  },

  // Obtener un colaborador por ID
  async getCollaboratorById(id: string): Promise<Collaborator> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    return response.data?.data || response.data;
  },

  // Crear nuevo colaborador
  async createCollaborator(collaboratorData: CollaboratorCreateDTO): Promise<Collaborator> {
    const response = await privateApi.post(`${API_BASE}`, collaboratorData);
    return response.data?.data || response.data;
  },

  // Actualizar colaborador
  async updateCollaborator(
    id: string,
    collaboratorData: CollaboratorUpdateDTO
  ): Promise<Collaborator> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, collaboratorData);
    return response.data?.data || response.data;
  },

  // Eliminar colaborador
  async deleteCollaborator(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },

  // Cambiar estado de un colaborador (toggle)
  async toggleCollaboratorStatus(id: string): Promise<Collaborator> {
    const response = await privateApi.patch(`${API_BASE}/${id}/toggle-status`);
    return response.data?.data || response.data;
  },

  // Obtener estadísticas de colaboradores
  async getCollaboratorStats(): Promise<CollaboratorStats> {
    const response = await privateApi.get(`${API_BASE}/stats`);
    return response.data?.data || response.data;
  },
};

export default collaboratorsApiService;

